<template>
    <div class="flex flex-col">
        <div class="w-full flex">
            <div :class="[hideLateralDescriptionValue ? 'w-full' : 'w-1/2 pr-2', 'flex items-center gap-2', inputClass]">
                <Label :for="`control-${controlId}`">
                    {{ config.display_name }}
                </Label>
                <Switch
                    v-model="controlValue"
                    :id="`control-${controlId}`"
                    :name="`control-${controlId}`"
                    :disabled="!editMode || props.config.readonly || props.config.value?.readonly" />
                <Tooltip v-if="config.description && hideLateralDescriptionValue">
                    <TooltipTrigger asChild>
                        <span>
                            <Info class="h-4 w-4 text-muted-foreground" />
                        </span>
                    </TooltipTrigger>
                    <TooltipContent side="right" class="whitespace-normal max-w-xs">
                        <p class="text-xs" v-html="sanitizeHtml(config.description)" />
                    </TooltipContent>
                </Tooltip>
                <div v-if="errors.length" class="text-destructive mt-1 text-xs">
                    <span v-for="error in errors" :key="error.$uid">* {{ error.$message }}</span>
                </div>
            </div>
            <div v-if="!hideLateralDescriptionValue" class="w-1/2 pl-2">
                <Alert
                    v-if="showTextInfoElement"
                    variant="default"
                    class="min-h-[calc(theme(spacing.10)+theme(spacing.6))] flex items-center p-3 text-xs border bg-muted/10">
                    <AlertDescription class="text-muted-foreground text-2xs">
                        <span v-html="sanitizeHtml(config.description)" />
                    </AlertDescription>
                </Alert>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, watch, onMounted, onUpdated, computed } from 'vue';
import { sanitizeHtml } from '@/utils/utils';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Info } from 'lucide-vue-next';
import { uuid4 } from '@sentry/utils';

const controlId = uuid4();

const emit = defineEmits(['input']);
const props = defineProps({
    config: Object,
    value: Boolean,
    editMode: { type: Boolean, default: true },
    overrideLabelOpacity: Boolean,
    hideLateralDescription: { type: Boolean, default: false },
    errors: { type: Array, default: () => [] },
    inputClass: { type: String, default: '' }
});

const hideLateralDescriptionValue = ref(props.hideLateralDescription);

onUpdated(() => {
    hideLateralDescriptionValue.value = props.hideLateralDescription ? props.hideLateralDescription : !props.editMode || window.innerWidth <= 1024;
});

const defaultValue = ref(props.config.value.default ?? false);
const controlValue = ref(props.value ?? defaultValue.value);

const handleResize = () => {
    hideLateralDescriptionValue.value = props.hideLateralDescription ? props.hideLateralDescription : window.innerWidth <= 1024;
};

const showTextInfoElement = computed(() => {
    return (
        props.config.description &&
        !hideLateralDescriptionValue.value &&
        props.config.display_name?.trim()?.toLowerCase() !== props.config.description?.trim()?.toLowerCase() &&
        props.config.description?.trim()?.split(' ').length > 1
    );
});

window.addEventListener('resize', handleResize);
onMounted(() => {
    handleResize();
});

watch(controlValue, (newValue) => {
    emit('input', newValue);
});
</script>

<style scoped>
.arrow-left::before {
    content: '';
    position: absolute;
    left: -12px;
    top: 50%;
    transform: translateY(-50%);
    width: 0;
    height: 0;
    border-top: 18px solid transparent;
    border-bottom: 18px solid transparent;
    border-right: 12px solid #bbf7d0;
}
</style>
